import { createORPCClient } from '@orpc/client'
import type { RouterClient } from '@orpc/server'
import { RPCLink } from '@orpc/client/fetch'
import { createTanstackQueryUtils } from '@orpc/tanstack-query'
import type { Router } from '../../../api/src/router'
import { useAuth } from '@/hooks/useAuth'
import { useMemo, useRef } from 'react'
import { getCachedCompanyId } from './company-context'

// Get API URL from environment variable
const API_URL = import.meta.env.VITE_API_URL
if (!API_URL) {
  throw new Error('Missing VITE_API_URL environment variable')
}

const REQUEST_TIMEOUT_MS = 15_000

// App version (apps/app/package.json, injected at build by Vite — see
// vite.config.ts). Sent as `x-app-version` on every request from BOTH the iOS
// app and the web app so the API can force-gate outdated clients.
const APP_VERSION = import.meta.env.VITE_APP_VERSION as string | undefined

// Give every request a timeout so a fetch interrupted by system sleep / app
// background can't hang forever (which would otherwise keep the query stuck in
// `fetchStatus: 'fetching'` and block all future fetches). Compose React Query's
// own abort signal with the timeout WITHOUT AbortSignal.any (iOS 17.4+) — a
// manual controller works on the iOS 16 target.
export function fetchWithTimeout(
  request: Request,
  init: { redirect?: Request['redirect'] },
  options: { signal?: AbortSignal }
): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(
    () => controller.abort(new DOMException('Request timed out', 'TimeoutError')),
    REQUEST_TIMEOUT_MS
  )

  const upstream = options.signal ?? request.signal // React Query cancellation
  if (upstream) {
    if (upstream.aborted) controller.abort(upstream.reason)
    else
      upstream.addEventListener('abort', () => controller.abort(upstream.reason), {
        once: true,
      })
  }

  // Rebuild the Request: a Request's .signal is immutable post-construction, so
  // passing { signal } to fetch(request, ...) does not reliably override it in
  // WebKit.
  return fetch(
    new Request(request, { signal: controller.signal, redirect: init.redirect })
  ).finally(() => clearTimeout(timeoutId))
}

// Unauthenticated oRPC client for startup-time calls that must work before
// sign-in (e.g. the force-update version check). Sends only `x-app-version`.
export const publicOrpcClient: RouterClient<Router> = createORPCClient(
  new RPCLink({
    url: API_URL,
    fetch: (request, init, options) => fetchWithTimeout(request, init, options),
    headers: () => ({ 'x-app-version': APP_VERSION }),
  })
)

// Hook to get authenticated oRPC client
export const useOrpcClient = () => {
  const { getToken } = useAuth()

  // Keep a ref so the headers() closure always calls the latest getToken
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  return useMemo(
    () => {
      const link = new RPCLink({
        url: API_URL,
        fetch: (request, init, options) => fetchWithTimeout(request, init, options),
        async headers() {
          try {
            const token = await getTokenRef.current()
            const companyId = getCachedCompanyId()
            return {
              Authorization: token ? `Bearer ${token}` : undefined,
              'x-company-id': companyId ?? undefined,
              'x-app-version': APP_VERSION,
            }
          } catch (error) {
            console.error('[oRPC] Failed to get auth token:', error)
            return {}
          }
        },
      })

      const orpc: RouterClient<Router> = createORPCClient(link)
      return orpc
    },
    [] // Stable — headers() reads getTokenRef.current per-request
  )
}

// Hook to get TanStack Query utilities with auth
export const useOrpcTanstackQueryUtils = () => {
  const client = useOrpcClient()
  return useMemo(
    () => createTanstackQueryUtils(client),
    [client]
  )
}
